import { combineEpics, ofType } from "redux-observable";
import {
    debounceTime,
    map,
    mergeMap,
    switchMap,
    withLatestFrom,
} from "rxjs/operators";
import { from, of } from "rxjs";
import {
    setCasingDriftID,
    setCasingID,
    setCasingWeightList,
    setLinerID,
    setLinerOD,
    setLinerODList,
    setLinerWeight,
    setLinerWeightList,
    setTubingID,
    setTubingWeight,
    setTubingWeightList,
} from "../../../store/calculation/calculation-input/TubingSlice";
import { RootState } from "../../../store/ConfigureStore";
import {
    getCasingDriftID,
    getCasingID,
    getCasingWeightList,
    getLinerID,
    getLinerODList,
    getLinerWeightList,
    getTubingID,
    getTubingWeightList,
} from "../../../services/calculations/calculation-input/tubing.service";

const fetchCasingWeightListEpic = (action$: any, state$: any) => {
    return action$.pipe(
        ofType("tubing/setCasingOD"),
        debounceTime(1000),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.tubing?.tab1Input))
        ),
        switchMap(([_, tab1Input]) => {
            return from(
                getCasingWeightList(
                    +tab1Input?.unitLinerWeightListVal,
                    +tab1Input?.casingOD
                )
            ).pipe(
                mergeMap((data) => {
                    const records = data?.CasingWeightList;
                    if (!records) return [];
                    return [
                    setCasingWeightList(records),
                    setCasingID(""),
                    setCasingDriftID(""),
                    setLinerOD(""),
                    setLinerID(""),
                    setLinerWeight(""),
                ]})
            );
        })
    );
};

const fetchLinerODList = (action$: any, state$: any) => {
    return action$.pipe(
        ofType("tubing/setCasingOD"),
        debounceTime(1000),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state.tubing.tab1Input))
        ),
        switchMap(([_, tab1Input]) => {
            return from(getLinerODList(tab1Input?.casingOD ?? 0)).pipe(
                mergeMap((data) => {
                    const records = data?.LinerODList;
                    if (!records) return [];
                    return [setLinerODList(records)];
                })
            );
        })
    );
};

const fetchCasingIDEpic = (action$: any, state$: any) => {
    return action$.pipe(
        ofType("tubing/setCasingWeight"),
        debounceTime(1000),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state.tubing.tab1Input))
        ),
        switchMap(([_, tab1Input]) => {
            return from(
                getCasingID(
                    +tab1Input?.unitCasingIDListVal,
                    +tab1Input?.unitCasingWeightListVal,
                    tab1Input?.casingOD ?? 0,
                    tab1Input?.casingWeight ?? 0
                )
            ).pipe(mergeMap((data) => [setCasingID(data)]));
        })
    );
};

const fetchCasingDriftIDEpic = (action$: any, state$: any) => {
    return action$.pipe(
        ofType("tubing/setCasingWeight"),
        debounceTime(1000),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state.tubing.tab1Input))
        ),
        switchMap(([_, tab1Input]) => {
            return from(
                getCasingDriftID(
                    +tab1Input?.unitCasingIDListVal,
                    +tab1Input?.unitCasingWeightListVal,
                    tab1Input?.casingOD ?? 0,
                    tab1Input?.casingWeight ?? 0
                )
            ).pipe(mergeMap((data) => [setCasingDriftID(data)]));
        })
    );
};

const fetchLinerWeightListEpic = (action$: any, state$: any) => {
    return action$.pipe(
        ofType("tubing/setLinerOD"),
        debounceTime(1000),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state.tubing.tab1Input))
        ),
        switchMap(([_, tab1Input]) => {
            return from(
                getLinerWeightList(
                    +tab1Input?.unitLinerWeightListVal,
                    tab1Input?.linerOD ?? 0
                )
            ).pipe(
                mergeMap((data) => {
                    const records = data?.LinerWeightList;
                    if (!records) return [];
                    return [
                        setLinerWeightList(records),
                        setLinerID(""),
                        setLinerWeight(""),
                    ];
                })
            );
        })
    );
};

const fetchLinerIDEpic = (action$: any, state$: any) => {
    return action$.pipe(
        ofType("tubing/setLinerWeight"),
        debounceTime(1000),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state.tubing.tab1Input))
        ),
        switchMap(([_, tab1Input]) => {
            return from(
                getLinerID(
                    +tab1Input?.unitLinerIDListVal,
                    +tab1Input?.unitLinerWeightListVal,
                    tab1Input?.linerOD ?? 0,
                    tab1Input?.linerWeight ?? 0
                )
            ).pipe(mergeMap((data) => [setLinerID(data)]));
        })
    );
};

const fetchTubingODEpic = (action$: any, state$: any) => {
    return action$.pipe(
        ofType("tubing/setTubingOD"),
        debounceTime(1000),
        switchMap((actionData) => {
            return of(1).pipe(mergeMap(() => [setTubingWeight(""), setTubingID("")]));
        })
    );
};

const fetchTubingWeightListEpic = (action$: any, state$: any) => {
    return action$.pipe(
        ofType("tubing/setTubingTypeUpset", "tubing/setTubingOD"),
        debounceTime(1000),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state.tubing.tab1Input))
        ),
        switchMap(([_, tab1Input]) => {
            return from(
                getTubingWeightList(
                    Number(tab1Input?.unitTubingWeightListVal) ?? 0,
                    tab1Input?.tubingTypeUpset,
                    tab1Input?.tubingOD ?? 0
                )
            ).pipe(
                mergeMap((data) => {
                    const records = data?.TubingWeightList;
                    if (!records) return [];
                    return [setTubingWeightList(records)];
                })
            );
        })
    );
};

export const fetchTubingIDEpic = (action$: any, state$: any) => {
    return action$.pipe(
        ofType("tubing/setTubingWeight"),
        debounceTime(1000),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state.tubing.tab1Input))
        ),
        switchMap(([_, tab1Input]) => {
            return from(
                getTubingID(
                    +tab1Input?.unitTubingIDListVal,
                    +tab1Input?.unitTubingWeightListVal,
                    tab1Input?.tubingOD,
                    tab1Input?.tubingWeight
                )
            ).pipe(mergeMap((data) =>{
                return  [setTubingID(data)]
            }));
        })
    );
};

export const tubingEpic = combineEpics(
    fetchLinerODList,
    fetchLinerIDEpic,
    fetchCasingIDEpic,
    fetchTubingODEpic,
    fetchTubingIDEpic,
    fetchCasingDriftIDEpic,
    fetchLinerWeightListEpic,
    fetchTubingWeightListEpic,
    fetchCasingWeightListEpic,
);
